import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { updateUserProfile } from '../../../services/userService';

const UserProfile = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    location: '',
    timezone: 'America/New_York',
    language: 'English',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
  });

  useEffect(() => {
    if (userProfile && user) {
      setProfileData({
        fullName: userProfile?.fullName || '',
        email: user?.email || '',
        phone: userProfile?.phone || '',
        role: userProfile?.role || 'viewer',
        department: userProfile?.department || '',
        location: userProfile?.location || '',
        timezone: userProfile?.timezone || 'America/New_York',
        language: userProfile?.language || 'English',
        dateFormat: userProfile?.dateFormat || 'MM/DD/YYYY',
        currency: userProfile?.currency || 'USD',
      });
    }
  }, [userProfile, user]);

  const roleOptions = [
    { value: 'admin', label: 'Administrator' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' },
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  ];

  const languageOptions = [
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'JPY', label: 'JPY (¥)' },
  ];

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await updateUserProfile(user?.id, {
        full_name: profileData?.fullName,
        phone: profileData?.phone,
        department: profileData?.department,
        location: profileData?.location,
        timezone: profileData?.timezone,
        language: profileData?.language,
        date_format: profileData?.dateFormat,
        currency: profileData?.currency,
      });

      await refreshProfile();
      setSuccess(true);
      setIsEditing(false);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (userProfile && user) {
      setProfileData({
        fullName: userProfile?.fullName || '',
        email: user?.email || '',
        phone: userProfile?.phone || '',
        role: userProfile?.role || 'viewer',
        department: userProfile?.department || '',
        location: userProfile?.location || '',
        timezone: userProfile?.timezone || 'America/New_York',
        language: userProfile?.language || 'English',
        dateFormat: userProfile?.dateFormat || 'MM/DD/YYYY',
        currency: userProfile?.currency || 'USD',
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const getInitials = () => {
    const name = profileData?.fullName || 'User';
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            User Profile
          </h2>
          <p className="text-sm text-muted-foreground font-caption mt-1">
            Manage your personal information and preferences
          </p>
        </div>
        {!isEditing && (
          <Button
            variant="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md flex items-center gap-2">
          <Icon name="AlertCircle" size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500 text-green-600 px-4 py-3 rounded-md flex items-center gap-2">
          <Icon name="CheckCircle" size={18} />
          <span className="text-sm">Profile updated successfully!</span>
        </div>
      )}

      <div className="bg-card border border-border rounded-md p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center justify-center w-24 h-24 md:w-32 md:h-32 rounded-full bg-primary/10 text-primary text-3xl md:text-4xl font-heading font-semibold">
              {getInitials()}
            </div>
          </div>

          <div className="flex-1 space-y-4 md:space-y-6">
            <div>
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  value={profileData?.fullName}
                  onChange={(e) =>
                    setProfileData({ ...profileData, fullName: e?.target?.value })
                  }
                  disabled={!isEditing}
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={profileData?.email}
                  disabled={true}
                  helperText="Email cannot be changed"
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={profileData?.phone}
                  onChange={(e) =>
                    setProfileData({ ...profileData, phone: e?.target?.value })
                  }
                  disabled={!isEditing}
                />
                <Input
                  label="Role"
                  type="text"
                  value={profileData?.role?.toUpperCase()}
                  disabled={true}
                  helperText="Role is managed by administrators"
                />
              </div>
            </div>

            <div>
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
                Professional Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Department"
                  type="text"
                  value={profileData?.department}
                  onChange={(e) =>
                    setProfileData({ ...profileData, department: e?.target?.value })
                  }
                  disabled={!isEditing}
                />
                <Input
                  label="Location"
                  type="text"
                  value={profileData?.location}
                  onChange={(e) =>
                    setProfileData({ ...profileData, location: e?.target?.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div>
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
                Preferences
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Timezone"
                  options={timezoneOptions}
                  value={profileData?.timezone}
                  onChange={(value) =>
                    setProfileData({ ...profileData, timezone: value })
                  }
                  disabled={!isEditing}
                />
                <Select
                  label="Language"
                  options={languageOptions}
                  value={profileData?.language}
                  onChange={(value) =>
                    setProfileData({ ...profileData, language: value })
                  }
                  disabled={!isEditing}
                />
                <Select
                  label="Date Format"
                  options={dateFormatOptions}
                  value={profileData?.dateFormat}
                  onChange={(value) =>
                    setProfileData({ ...profileData, dateFormat: value })
                  }
                  disabled={!isEditing}
                />
                <Select
                  label="Currency"
                  options={currencyOptions}
                  value={profileData?.currency}
                  onChange={(value) =>
                    setProfileData({ ...profileData, currency: value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex gap-3 pt-4 border-t border-border">
                <Button
                  variant="default"
                  iconName="Check"
                  iconPosition="left"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
          Account Information
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-muted-foreground">User ID</span>
            <span className="text-sm font-mono text-foreground">{user?.id?.slice(0, 8)}...</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-muted-foreground">Account Created</span>
            <span className="text-sm text-foreground">
              {user?.created_at ? new Date(user?.created_at)?.toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-muted-foreground">Last Sign In</span>
            <span className="text-sm text-foreground">
              {user?.last_sign_in_at ? new Date(user?.last_sign_in_at)?.toLocaleString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;